<template>
  <div>
    <div
      v-if="showArrow"
      class="arrow"
    />
    <HintBox
      :show-hint-box="showHintBox"
      :hints="hints"
      show-icon-big
      color-icon
      @closeHint="showHintBox = false"
      @updateHintPreference="updateHintPreference"
    />
    <Loading v-if="loading" />

    <div
      v-else-if="!filterSqlScript && isShared"
      class="no-queries-msg"
    >
      The shared Golden Query does not exist.
    </div>
    <div
      v-else
      class="golden-query-content"
    >
      <div
        class="golden-query-nav"
        :style="{ '--sticky-tab-top': `${contentTop}px` }"
      >
        <Tabs
          v-model="currentTabPane"
          name="goldenQuery"
          :animated="false"
          class="golden-query-tab"
        >
          <TabPane
            :name="officialTabPaneName"
            tab="goldenQuery"
            :disabled="tabDisabled[officialTabPaneName]"
            :label="officialTabLabel"
          />
          <TabPane
            :name="communityTabPaneName"
            tab="goldenQuery"
            :disabled="tabDisabled[communityTabPaneName]"
            :label="communityTabLabel"
          />
          <FilterPanel
            v-if="!isShared"
            slot="extra"
            ref="filter-panel"
            :filter-text="filterText"
            :tags-list="tagsList"
            :selected-tags="selectedTags"
            :show-tag-control="false"
            :tag-condition="tagCondition"
            :owners-list="ownersList"
            :selected-owner="selectedOwner"
            :show-favorites-control="true"
            :is-filter-by-favorites="isFilterByFavorites"
            @onFilterResults="filterResults"
          >
            <template #top>
              <div class="row">
                <div
                  class="element-row action"
                  @click="filterResults"
                >
                  <p>Include Queries in filtering</p>
                  <Checkbox
                    v-model="includeQueryInFilter"
                    class="right"
                  />
                </div>
              </div>
            </template>
          </FilterPanel>
        </Tabs>
      </div>
      <div class="query-template-container">
        <div
          v-if="!filterSqlScript.length"
          class="no-queries-msg"
        >
          No Golden Queries to Display.
          <div v-if="hasFilterSet">
            Please double check the
            <a
              @mouseover="showArrow=true"
              @mouseleave="showArrow=false"
            >
              filter setting
            </a>
            on the top right.
          </div>
        </div>
        <div
          v-for="(query, index) in filterSqlScriptForPage"
          v-else
          :key="`${index}.${query.name}`"
          style="margin-bottom:16px"
        >
          <QueryTemplate
            :query="query"
            :golden-query-params="goldenQueryParams"
            @tag-selected="toggleTag"
            @query-share="onShareLink"
            @copy-to-query-pad="onCopyToQueryPadIconClick"
            @copy-query="copyQuery"
            @fav-selected="toggleFavorite"
            @feedback="onSendFeedback"
          />
        </div>
      </div>
    </div>
    <Page
      v-show="filterSqlScript.length > pageSize"
      :current="currentPage + 1"
      :total="filterSqlScript.length"
      :page-size="pageSize"
      show-elevator
      @on-change="onPageChange"
    />
  </div>
</template>
<script>
import QueryTemplate from './helper-components/QueryTemplate'
import FilterPanel from './helper-components/FilterPanel'
import HintBox from './helper-components/HintBox'
import Loading from './helper-components/Loading'
import { setUpQueryFormat } from '@/helpers/query-formatter'
import axios from 'axios'
import _ from 'lodash'

const OFFICIAL_TABPANE_NAME = 'official'
const COMMUNITY_TABPANE_NAME = 'community'
const TAB_TO_COMPONENT = {
  official: 'golden_query',
  community: 'community_query'
}

export default {

  components: {
    QueryTemplate,
    FilterPanel,
    HintBox,
    Loading
  },

  props: {
    userId: Number,
    contentTop: Number,
    initSearchValue: String // The initial search value (from Data Catalog)
  },

  data () {
    const dummySqlScripts = [...Array(5).keys()].map((val) => ({
      name: `Golden Query ${val}`,
      author: {
        name: 'Some Body',
        email: 'some.body@email.com'
      },
      tags: [],
      date: '2000-01-01',
      description: `This is Golden Query ${val}`,
      show: 'template',
      lines: '',
      params: [{
        name: 'input',
        description: 'input for golden query'
      }]
    }))

    return {
      officialTabLabel: (h) => {
        return h('Tooltip', {
          props: {
            content: 'Validated Golden Queries',
            placement: 'bottom',
            transfer: true
          }
        }, ['Official'])
      },
      communityTabLabel: (h) => {
        return h('Tooltip', {
          props: {
            content: 'Other Shared Golden Queries',
            placement: 'bottom',
            transfer: true
          }
        }, ['Community'])
      },
      context: 'golden_query_library',
      loading: true,
      sqlScripts: {
        official: dummySqlScripts,
        community: dummySqlScripts
      },
      currentTabPane: OFFICIAL_TABPANE_NAME,
      tabDisabled: {
        official: false,
        community: false
      },
      officialTabPaneName: OFFICIAL_TABPANE_NAME,
      communityTabPaneName: COMMUNITY_TABPANE_NAME,
      tabToComponent: TAB_TO_COMPONENT,
      queriesTags: {},
      queriesOwners: {},
      tagsList: [],
      selectedTags: this.$root.preferences.goldenQuery.selectedTags || [],
      ownersList: [],
      selectedOwner: this.$root.preferences.goldenQuery.selectedOwner || '',
      cmOptions: {
        tabSize: 4,
        mode: 'text/x-sql',
        lineNumbers: true,
        readOnly: true,
        autoRefresh: true
      },
      openedSections: [],
      filterText: this.initSearchValue || this.$root.preferences.goldenQuery.filterText || '',
      currentPage: 0,
      includeQueryInFilter: this.$root.preferences.goldenQuery.includeQueryInFilter || true,
      pageSize: 5,
      tagCondition: this.$root.preferences.goldenQuery.tagFilterClause || 'and',
      isShared: false,
      desiredItem: null,
      isFilterByFavorites: this.$root.preferences.goldenQuery.isFilterByFavorites || false,
      showHintBox: false,
      hintsPreferences: dummySqlScripts,
      hints: [
        {
          id: 1,
          title: 'Hint',
          type: 'info',
          description: `Do you want to filter by <b>Keywords, Tags, Owner</b> or <b>Favorites</b>? Just click on the filter icon <i class="fas fa-filter" />.`,
          hidden: false
        }
      ],
      goldenQueryPrams: {},
      showArrow: false
    }
  },

  computed: {
    hasFilterSet () {
      return this.filterText.length || this.tagsList.some(tag => tag.selected) || this.isFilterByFavorites || this.selectedOwner
    },

    filterSqlScript () {
      const filterTarget = this.sqlScripts[this.currentTabPane]

      if (this.loading || this.isShared) {
        return filterTarget
      }
      const filterText = this.filterText.toLowerCase()
      const noTagSelected = !this.tagsList.some(tag => tag.selected)

      if (!this.hasFilterSet) {
        return filterTarget
      }

      return filterTarget.filter(query => {
        return (
          (
            query.name.toLowerCase().match(filterText) ||
              this.authorMatch(query, filterText) ||
              (this.includeQueryInFilter && this.queryMatch(query)) ||
              query.description.toLowerCase().match(filterText)
          ) &&
            (noTagSelected || this.evaluateSelectedTags(query.tags, this.tagCondition, this.selectedTags)) &&
            (!this.selectedOwner || this.authorMatch(query, this.selectedOwner)) &&
            (!this.isFilterByFavorites || this.matchFavorites(query.isFavorite))
        )
      })
    },

    filterSqlScriptForPage () {
      const startingIndex = this.currentPage * this.pageSize
      return this.filterSqlScript.slice(startingIndex, startingIndex + this.pageSize)
    }
  },

  watch: {
    userId () {
      this.setSqlScripts()
    },

    initSearchValue (newInitSearchVal) {
      this.filterText = newInitSearchVal || this.$root.preferences.goldenQuery.filterText
    },

    $route (newRoute) {
      if (newRoute.path === '/golden-query') {
        this.setSqlScripts()
        this.currentTabPane = this.officialTabPaneName
        Object.keys(this.tabDisabled).forEach(key => {
          this.tabDisabled[key] = false
        })
      }
    },

    currentTabPane () {
      this.currentPage = 0
      if (!this.$route.params.hasOwnProperty('id')) {
        this.$router.replace({
          path: `/golden-query/${this.currentTabPane}`
        })
      }
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('goldenQuery')) {
      this.$set(this.$root.preferences, 'goldenQuery', {})
    }

    if (this.$root.preferences.hasOwnProperty('goldenQueryLibrary')) {
      this.$set(this.$root.preferences, 'goldenQuery', this.$root.preferences.goldenQueryLibrary)
      delete this.$root.preferences.goldenQueryLibrary
    }

    // Set the filter "tag condition" as "AND" by default.
    this.$set(this.$root.preferences.goldenQuery, 'tagFilterClause', 'and')

    // Set the filter "include query in filter" as "TRUE" by default.
    this.$set(this.$root.preferences.goldenQuery, 'includeQueryInFilter', true)

    sessionStorage.setItem('context', 'golden_query_library')
  },

  created () {
    this.fetchGoldenQueryParams()
  },

  mounted () {
    if (this.userId) {
      this.setSqlScripts()
    }

    let previousHintValue = false
    if (this.$root.preferences.goldenQuery.hasOwnProperty('blockHint')) {
      previousHintValue = this.$root.preferences.goldenQuery.blockHint
      delete this.$root.preferences.goldenQuery.blockHint
    }

    this.hintsPreferences = this.$root.preferences.goldenQuery.hints
      ? JSON.parse(this.$root.preferences.goldenQuery.hints)
      : {}
    this.hints.forEach(hint => {
      if (!this.hintsPreferences[hint.id]) {
        const isHidden = hint.id === 1 && previousHintValue
        this.hintsPreferences[hint.id] = isHidden
      }
      this.$set(hint, 'hidden', this.hintsPreferences[hint.id])
    })
    this.$set(this.$root.preferences.goldenQuery, 'hints', JSON.stringify(this.hintsPreferences))
    this.showHintBox = this.hints.some(hint => !hint.hidden)
  },

  methods: {
    async fetchGoldenQueryParams () {
      try {
        this.goldenQueryParams = (await axios.get(process.env.VUE_APP_API_URI + '/golden-query/params')).data
      } catch (error) {
        console.error(`Failed to fetch golden query parameters: ${error}`)
        this.$Message.error(`Failed to fetch golden query parameters: ${error}`)
      }
    },

    /**
     * Fetches all the existing Golden Queries to be displayed
     */
    async setSqlScripts () {
      this.loading = true
      this.desiredItem = this.$route.params.hasOwnProperty('id') ? this.$route.params.id : null
      this.isShared = !_.isNil(this.desiredItem)
      const pathArray = this.$route.path.split('/')
      if (pathArray.length > 2) {
        this.currentTabPane = this.isShared ? pathArray[pathArray.length - 2] : pathArray[pathArray.length - 1]
      }

      const favoriteQueries = {}
      const tabKeys = Object.keys(this.tabToComponent)
      const favoriteQueryPromises = tabKeys.map(tab => this.fetchFavoriteQueries(this.tabToComponent[tab]))

      try {
        const favoriteQueryResults = await Promise.all(favoriteQueryPromises)
        tabKeys.forEach((tab, index) => {
          favoriteQueries[tab] = favoriteQueryResults[index]
        })

        await this.fetchTemplates(favoriteQueries)
        if (!_.isNil(this.desiredItem)) {
          this.filterSharedQueryTemplate()
        }

        this.tagsList = Object.keys(this.queriesTags).sort().map(tag => ({
          name: tag,
          selected: this.selectedTags.includes(tag)
        }))
        this.ownersList = Object.keys(this.queriesOwners).sort().map(owner => ({
          name: owner,
          selected: owner === this.selectedOwner
        }))
      } catch (error) {
        this.$Message.error(`Failed to get golden query favourites: ${error}`)
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetches all user favorite queries given the queryType, could be either
     * 'golden_query' or 'community_query', which is the component_type in the user_fav table
     * @param {String} queryType
     */
    async fetchFavoriteQueries (queryType) {
      const userFavoriteQueries = await axios.get(`${process.env.VUE_APP_API_URI}/users/${this.userId}/favorite`, {
        params: {
          componentType: queryType
        }
      })
      return userFavoriteQueries.data.reduce((object, item) => { return { ...object, [item.component_id]: item.id } }, {})
    },

    /**
     * Fetches All the query templates (including official and community query templates)
     * @param {Object} favoriteQueries
     */
    async fetchTemplates (favoriteQueries) {
      // Fetch golden query (official) from global variable
      const [sqlQueries, sqlQueriesCommunity] = await Promise.all([
        axios.get(process.env.VUE_APP_API_URI + '/golden-query/sql-queries'),
        axios.get(process.env.VUE_APP_API_URI + '/golden-query/templates')
      ])

      // Filter out inactive queries
      sqlQueries.data = sqlQueries.data.filter(query => query.status !== 'inactive')
      sqlQueriesCommunity.data = sqlQueriesCommunity.data.filter(query => query.status !== 'inactive')

      const generatedQueryParams = this.$root.preferences.goldenQuery.generatedQueryParams || null
      this.sqlScripts[this.officialTabPaneName] = sqlQueries.data.map(query => {
        const isLastGeneratedQuery = generatedQueryParams
          ? generatedQueryParams.file === query.file
          : false
        const params = query.params.map(param => {
          const fetchedParams = isLastGeneratedQuery ? generatedQueryParams.params.find(item => item.name === param.name) : {}
          const defaultValue = _.get(fetchedParams, 'value') ? fetchedParams.value : this.defaultParamValue(param)
          this.$set(param, 'value', defaultValue)
          return param
        })

        const queryTags = query.categories || []

        this.updateFilterWhileLoading(queryTags, query.author)

        const goldenQueryLines = setUpQueryFormat(query.lines)
        this.$set(query, 'tags', queryTags)
        this.$set(query, 'lines', goldenQueryLines)
        this.$set(query, 'params', params)
        this.$set(query, 'show', 'template')
        this.$set(query, 'collapseValue', '')
        this.$set(query, 'isFavorite', favoriteQueries[this.officialTabPaneName].hasOwnProperty(query.id))
        this.$set(query, 'favoriteId', favoriteQueries[this.officialTabPaneName][query.id] || null)
        return query
      })

      // Fetch query templates from mysql database
      this.sqlScripts[this.communityTabPaneName] = sqlQueriesCommunity.data.map(sqlQueryCommunity => {
        const query = {}
        const queryParams = JSON.parse(sqlQueryCommunity.parameters)
        const queryTags = sqlQueryCommunity.categories.split(',')
        query.id = sqlQueryCommunity.id
        query.name = sqlQueryCommunity.title
        query.description = sqlQueryCommunity.description
        query.tags = queryTags
        query.date = sqlQueryCommunity['created_at']
        query.author = { email: sqlQueryCommunity['author_email'], name: sqlQueryCommunity['author_name'] }
        query.params = queryParams

        this.updateFilterWhileLoading(queryTags, query.author)
        const queryTemplate = setUpQueryFormat(sqlQueryCommunity['query_template'])
        query.lines = queryTemplate
        query.show = 'template'
        query.collapseValue = ''
        query.isFavorite = favoriteQueries[this.communityTabPaneName].hasOwnProperty(query.id)
        query.favoriteId = favoriteQueries[this.communityTabPaneName][query.id] || null

        return query
      })
    },

    /**
     * Updates the tag and author pools while processing the queries
     * Will be triggered in each iteration
     * @param {Object} queryTags
     * @param {Object} authors
     */
    updateFilterWhileLoading (queryTags, authors) {
      queryTags.forEach(tag => {
        if (!this.queriesTags[tag]) {
          this.queriesTags[tag] = true
        }
      })

      if (Array.isArray(authors)) {
        authors.forEach(author => {
          if (author && !this.queriesOwners[author]) {
            this.queriesOwners[author] = true
          }
        })
      } else {
        const queryAuthor = authors.email || ''
        if (queryAuthor && !this.queriesOwners[queryAuthor]) {
          this.queriesOwners[queryAuthor] = true
        }
      }
    },

    /**
     * Parses the URL of shared query and filters the query content
     */
    filterSharedQueryTemplate () {
      const item = this.currentTabPane === this.officialTabPaneName
        ? this.sqlScripts[this.currentTabPane].find(x => x.file === `${this.desiredItem}.sql`)
        : this.sqlScripts[this.currentTabPane].find(x => x.id === parseInt(this.desiredItem))
      this.sqlScripts[this.currentTabPane] = item ? [item] : []
      Object.keys(this.sqlScripts).forEach(tab => {
        if (tab !== this.currentTabPane) {
          this.sqlScripts[tab] = []
          this.tabDisabled[tab] = true
        }
      })
    },

    /**
     * Sets the filters for the queries list.
     * @param {Object} filterParams
     */
    filterResults  (filterParams) {
      this.currentPage = 0
      const filterTarget = this.sqlScripts[this.currentTabPane]
      if (!filterTarget || !filterParams) {
        return
      }

      const {
        filterText = this.filterText,
        includeQueryInFilter = this.includeQueryInFilter,
        tagsList = this.tagsList,
        selectedTags = this.selectedTags,
        tagCondition = this.tagCondition,
        selectedOwner = this.selectedOwner,
        isFilterByFavorites = this.isFilterByFavorites,
        resetFilters
      } = filterParams

      this.filterText = filterText.toLowerCase()
      this.includeQueryInFilter = resetFilters ? true : includeQueryInFilter
      this.tagsList = tagsList
      this.selectedTags = selectedTags
      this.tagCondition = tagCondition.toLowerCase()
      this.selectedOwner = selectedOwner
      this.isFilterByFavorites = isFilterByFavorites

      this.$set(this.$root.preferences.goldenQuery, 'filterText', this.filterText)
      this.$set(this.$root.preferences.goldenQuery, 'includeQueryInFilter', this.includeQueryInFilter)
      this.$set(this.$root.preferences.goldenQuery, 'selectedTags', this.selectedTags)
      this.$set(this.$root.preferences.goldenQuery, 'tagFilterClause', this.tagCondition)
      this.$set(this.$root.preferences.goldenQuery, 'selectedOwner', this.selectedOwner)
      this.$set(this.$root.preferences.goldenQuery, 'isFilterByFavorites', this.isFilterByFavorites)
    },

    /**
     * Copies the generated query into the clipboard.
     * @param {Object} query
     */
    copyQuery (query) {
      const copyText = query.show === 'template' ? query.lines : query.queryStr

      if (query.show === 'generated' && !copyText) {
        this.$Message.error({
          content: 'Generate query before copying!',
          duration: 2
        })
      } else {
        this.$log('query_copy', query.name, { query: copyText })
        this.$copyText(copyText).then(
          this.onCopySuccess.bind(this),
          this.onCopyError.bind(this)
        )
      }
    },

    /**
     * Displays a success message if the copy to clipboard was successful.
     */
    onCopySuccess () {
      this.$Message.info({
        content: 'Copied to clipboard!',
        duration: 2
      })
    },

    /**
     * Displays an error message if the copy to clipboard failed.
     */
    onCopyError () {
      this.$Message.error({
        content: 'Error while copying to clipboard!',
        duration: 2
      })
    },

    /**
     * Checks if the any of the query's authors name/email matches the search value.
     * @param {Object} query
     * @return {Boolean}
     */
    authorMatch (query, filterText) {
      filterText = filterText.toLowerCase()
      const author = query.author

      if (Array.isArray(author)) {
        return author.some(a => a.name.toLowerCase().match(filterText) || a.email.toLowerCase().match(filterText))
      }

      return author.name.toLowerCase().match(filterText) || author.email.toLowerCase().match(filterText)
    },

    /**
     * Checks if any of the query lines of query matched the search value.
     * @param {Object} query
     * @return {Boolean}
     */
    queryMatch (query) {
      const filterText = this.filterText.toLowerCase()
      return query.lines.toLowerCase().match(filterText)
    },

    /**
     * Filter the results by the selected tag.
     * @param {String} tagName
     * @param {Boolean} isSelected
     */
    toggleTag (tagName, isSelected) {
      const tag = this.tagsList.find(tag => tag.name === tagName)
      this.$set(tag, 'selected', isSelected)
      if (!isSelected) {
        this.selectedTags = this.selectedTags.filter(function (tag) {
          return tag !== tagName
        })
      } else if (!this.selectedTags.includes(tagName)) {
        this.selectedTags.push(tagName)
      }
      this.onFilterChange()
      this.$set(this.$root.preferences.goldenQuery, 'selectedTags', this.selectedTags)
    },

    /**
     * If "tagCondition" is "and", it returns TRUE if the first specified array contains all elements from the second one. Otherwise returns FALSE.
     * If "tagCondition" is "or", it returns TRUE if the first specified array contains some elements from the second one. Otherwise returns FALSE.
     * @param {Array} superset
     * @param {String} tagCondition
     * @param {Array} subset
     * @return {Boolean}
     */
    evaluateSelectedTags (superset, tagCondition, subset) {
      if (superset === undefined || subset.length === 0) {
        return false
      }

      if (tagCondition === 'and') {
        return subset.every(value => superset.indexOf(value) >= 0)
      }

      return subset.some(value => superset.indexOf(value) >= 0)
    },

    /**
     * Changes the current page to a new desired page.
     * @param {Number} pageNum
     */
    onPageChange (pageNum) {
      this.currentPage = pageNum - 1
      this.hideAllParamPanels()
      this.hideAllQueryTables()
    },

    /**
     * Initializes a codemirror on ready and on blur.
     * @param {Object} cm
     */
    onCmBlur (cm) {
      cm.off('copy', () => {})
      cm.setSize(null, 100)
    },

    /**
     * Specifies the actions to do if user focuses a codemirror.
     * @param {Object} cm
     */
    onCmFocus (cm) {
      cm.on('copy', (cm, e) => {
        this.$log('query_copy', e.srcElement.value, { request: 'Shortcut copy from Golden Query.' })
      })
      cm.setSize(null, 300)
    },

    /**
     * Initialize the current page and hides the parameters panels and the queries results
     * if the search box is modified.
     */
    onFilterChange () {
      this.currentPage = 0
      this.hideAllParamPanels()
      this.hideAllQueryTables()
    },

    /**
     * Submits a Golden Query to the Data Eng team in order to be approved.
     */
    submitGoldenQuery () {
      const email = 'data-eng-team@li.me'
      const subject = 'Golden Query Submission'
      const win = window.open(
        `https://mail.google.com/mail/?extsrc=mailto&url=mailto%3A%3Fto%3D${email}%26subject%3D${subject}`,
        '_blank'
      )

      win.focus()
    },

    /**
     * Hides the query results.
     * @param {Object} query
     */
    hideResult (query) {
      this.$set(query, 'showResult', false)
    },

    /**
     * Copies the share link into the clipboard.
     * @param {String} fileName
     * @param {Number} queryId
     */
    onShareLink (fileName, queryId) {
      const prefix = `${window.location.origin}/golden-query/${this.currentTabPane}/`
      const append = this.currentTabPane === this.officialTabPaneName ? fileName.slice(0, -4) : queryId
      const linkToShare = `${prefix}/${append}`
      this.$log('query_share', append, { queryType: this.currentTabPane })
      this.$copyText(linkToShare).then(
        this.onCopySuccess.bind(this),
        this.onCopyError.bind(this)
      )
    },

    onSendFeedback (fileName, queryId, queryOwnerEmail, queryName) {
      const append = this.currentTabPane === this.officialTabPaneName ? fileName.slice(0, -4) : queryId
      const path = `${this.$route.path}/${append}`
      this.$feedback(queryOwnerEmail, queryName, 'golden query', path)
    },

    /**
     * Copies the generated query into a new tab in Query Pad.
     * @param {Object} query
     */
    onCopyToQueryPadIconClick (query) {
      if (!query.queryStr) {
        this.$Message.error({
          content: 'Must fill the mandatory inputs before copying over to Query Pad!',
          duration: 5
        })

        return
      }

      const logExtra = {
        request: 'Edit Golden Query in Query Pad.',
        options: {}
      }
      query.params.forEach(param => {
        logExtra.options[param.name] = param.value
      })
      this.$log('query_transport', query.name, logExtra)

      this.$router.push({
        name: 'Query Pad',
        params: {
          goldenQuery: query.queryStr
        }
      })
    },

    /**
     * Provides the default value for a param depending on its type
     * @param {Object} param
     * @return {String/Number} The return value depends on the param type
     */
    defaultParamValue (param) {
      if (param.default) {
        return param.default
      } else if (param.type === 'number' || param.type.includes('decimal')) {
        return 0
      }

      return ''
    },

    /**
     * Hides the Parameters panel of a specific query.
     * @param {Object} query
     */
    hideParamPanel (query) {
      this.$nextTick(() => {
        this.$set(query, 'collapseValue', '')
      })
    },

    /**
     * Hides all the parameters panels existing in the current page.
     */
    hideAllParamPanels () {
      Object.values(this.sqlScripts).forEach(val => {
        val.forEach(query => this.hideParamPanel(query))
      })
    },

    /**
     * Hides all query results existing in the current page.
     */
    hideAllQueryTables () {
      Object.values(this.sqlScripts).forEach(val => {
        val.forEach(query => this.hideResult(query))
      })
    },

    /**
     * Updates the user preference such as "LIKES" or "FAVORITES" on an specific dashboard
     * @param {Number} queryId
     * @param {Object} properties Contains the properties regarding the "favorite" component (isActive, id).
     */
    toggleFavorite (queryId, properties) {
      const templateTypeToFavorite = this.tabToComponent[this.currentTabPane]
      const templateToFavorite = this.sqlScripts[this.currentTabPane].find(query => query.id === queryId)
      const method = properties.isActive ? 'post' : 'delete'
      const dataParams = properties.isActive ? {
        userId: this.userId,
        componentType: templateTypeToFavorite,
        componentId: queryId
      } : {
        id: properties.id,
        componentType: templateTypeToFavorite
      }
      axios({
        method: method,
        url: `${process.env.VUE_APP_API_URI}/users/favorite`,
        headers: {
          'Content-Type': 'application/json'
        },
        data: dataParams
      })
        .then(result => {
          if (result.data.hasOwnProperty('error')) {
            throw new Error(result.data.error.message)
          }

          this.$set(templateToFavorite, 'isFavorite', properties.isActive)
          const logLabel = properties.isActive ? `${templateTypeToFavorite}_favorite` : `${templateTypeToFavorite}_unfavorite`

          if (result.data.length > 0) {
            this.$set(templateToFavorite, 'favoriteId', result.data[0].id)
          }
          this.$log(logLabel, templateToFavorite.name, { queryId: queryId })
        })
    },

    /**
     * Validates if the current query is favorite
     * @param {Boolean} favorite
     * @return {Boolean} "TRUE" ONLY if it is required filter by favorites AND current query is favorite.
     */
    matchFavorites (favorite) {
      return this.isFilterByFavorites && favorite
    },

    /**
     * Updates the preference about to block hints.
     * @param {Number} hintId
     * @param {Boolean} isHidden
     */
    updateHintPreference (hintId, isHidden) {
      this.hintsPreferences[hintId] = isHidden
      const updatedHint = this.hints.find(hint => hint.id === hintId)
      this.$set(updatedHint, 'hidden', isHidden)
      this.$set(this.$root.preferences.goldenQuery, 'hints', JSON.stringify(this.hintsPreferences))
    }
  }
}
</script>

<style lan='sass' scoped>
  .col-left {
    width: calc(100% - 344px);
  }

  .col-right {
    width: 336px;
    margin-left: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .param-input {
    margin-bottom: 16px;
  }

  .generate-query-button-container {
    display: flex;
    justify-content: flex-end;
  }

  .query-container {
    height: 100%;
    width: 100%;
    border-style: solid;
  }

  .query-param-autocomplete >>> .ivu-auto-complete.ivu-select-dropdown {
    max-height: 250px;
  }

  .param-form-item >>> .ivu-form-item-content {
    width: 100%;
  }

  .no-queries-msg {
    text-align: center;
    font-size: 32px;
    margin-top: 15%;
    margin-bottom: 15%;
  }

  .tag {
    max-width: unset;
    border-color: var(--color-card-border);
    background-color: transparent;
    cursor: pointer;
  }

  .tag >>> .ivu-tag-text {
    color: var(--color-black);
  }

  .tags-row {
    display: flex;
    flex-direction: row;
  }

  .search-box >>> .ivu-input {
    height: 40px;
  }

  .email-icon {
    margin: 0;
    margin-right: 8px;
    box-sizing: border-box;
    display: flex;
    align-items: baseline;
    justify-content: center;
    color: var(--color-green-lime);
  }

  .email-icon i {
    font-size: 20px;
    cursor: pointer;
  }

  .parameter {
    margin-bottom: 16px;
  }

  .parameter >>> .ivu-form-item-content {
    line-height: 0;
  }

  .action-icon {
    cursor: pointer;
    font-size: 16px;
  }

  .query-icon-container div {
    width: 18px;
    text-align: center;
  }

  .query-icon-container div:not(:last-child) {
    margin-right: 8px;
  }

  .filter .dropdown .elements .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .filter .dropdown .elements .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row .select-input {
    width: 100%;
  }

  .filter .dropdown .elements .row .select-input.left >>> .ivu-select-selection {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.right >>> .ivu-select-selection {
    width: 100%;
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.big-list >>> .ivu-select-dropdown {
    transform-origin: center top !important;
    top: unset !important;
    max-height: 400px;
    overflow-y: scroll;
  }

  .filter .dropdown .elements .row .select-input >>> .ivu-select-not-found {
    color: var(--color-red-alert);
    font-size: 12px;
    list-style: none;
    text-align: left;
    padding: 7px 16px;
  }

  .ivu-tabs {
    overflow: unset;
  }

  .golden-query-nav {
    position: sticky;
    top: var(--sticky-tab-top);
    z-index: 100;
    background-color: var(--color-body-background);
  }

  .golden-query-tab >>> .ivu-tabs-bar {
    margin-bottom: 0px;
  }

  .query-template-container {
    margin-top: 16px;
  }

  .tab-info {
    padding-top: 10px;
    font-size: 14px;
  }

.arrow,
.arrow:before {
  position: absolute;
  left: 100%;
}

.arrow {
  width: 40px;
  height: 40px;
  top: 10%;
  margin: -20px 0 0 -20px;
  -webkit-transform: rotate(225deg);
  border-left: none;
  border-top: none;
}

.arrow:before {
  content: "";
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border-left: none;
  border-top: none;
  border-right: 2px black solid;
  border-bottom: 2px black solid;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-name: arrow;
}

@keyframes arrow {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-10px, -10px);
  }
}
</style>
